// Client-side rate limiting with localStorage persistence
class EnhancedRateLimiter {
    constructor() {
        this.limits = {
            auth: { max: 5, window: 300000, message: 'Too many login attempts. Please wait 5 minutes.' },
            booking: { max: 10, window: 3600000, message: 'Too many booking requests. Please wait before trying again.' },
            profile: { max: 20, window: 3600000, message: 'Too many profile updates. Please wait before trying again.' },
            slot: { max: 50, window: 3600000, message: 'Too many slot additions. Please wait before trying again.' }
        };
        this.storage = window.localStorage;
        this.cleanupOldEntries();
    }
    
    getKey(action, userId) {
        return `rateLimit_${action}_${userId || 'anonymous'}`;
    }
    
    isAllowed(action, userId = null) {
        const limit = this.limits[action];
        if (!limit) return true;
        
        const key = this.getKey(action, userId);
        const now = Date.now();
        
        // Get existing requests from localStorage
        let requests = [];
        try {
            const stored = this.storage.getItem(key);
            if (stored) {
                requests = JSON.parse(stored);
            }
        } catch (e) {
            console.error('Error parsing rate limit data:', e);
            requests = [];
        }
        
        // Filter out old requests
        requests = requests.filter(time => now - time < limit.window);
        
        // Check if limit exceeded
        if (requests.length >= limit.max) {
            const oldestRequest = Math.min(...requests);
            const timeUntilReset = (oldestRequest + limit.window) - now;
            const minutesLeft = Math.ceil(timeUntilReset / 60000);
            
            throw new Error(`${limit.message} (${minutesLeft} minutes remaining)`);
        }
        
        // Add new request
        requests.push(now);
        
        // Store updated requests
        try {
            this.storage.setItem(key, JSON.stringify(requests));
        } catch (e) {
            console.error('Error storing rate limit data:', e);
        }
        
        return true;
    }
    
    reset(action, userId = null) {
        const key = this.getKey(action, userId);
        this.storage.removeItem(key);
    }
    
    cleanupOldEntries() {
        const now = Date.now();
        const keysToRemove = [];
        
        // Iterate through all localStorage keys
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key && key.startsWith('rateLimit_')) {
                try {
                    const requests = JSON.parse(this.storage.getItem(key));
                    // Remove if all requests are older than 24 hours
                    if (Array.isArray(requests) && requests.every(time => now - time > 86400000)) {
                        keysToRemove.push(key);
                    }
                } catch (e) {
                    // Remove corrupted entries
                    keysToRemove.push(key);
                }
            }
        }
        
        // Remove old entries
        keysToRemove.forEach(key => this.storage.removeItem(key));
    }
    
    // Get remaining requests for an action
    getRemaining(action, userId = null) {
        const limit = this.limits[action];
        if (!limit) return null;
        
        const key = this.getKey(action, userId);
        const now = Date.now();
        
        try {
            const stored = this.storage.getItem(key);
            if (!stored) return limit.max;
            
            const requests = JSON.parse(stored);
            const validRequests = requests.filter(time => now - time < limit.window);
            
            return Math.max(0, limit.max - validRequests.length);
        } catch (e) {
            return limit.max;
        }
    }
    
    // Add custom limit
    addLimit(action, max, windowMs, message) {
        this.limits[action] = { max, window: windowMs, message };
    }
}

// Create singleton instance
const rateLimiter = new EnhancedRateLimiter();

// Auto-cleanup every hour
setInterval(() => {
    rateLimiter.cleanupOldEntries();
}, 3600000);

export default rateLimiter;